import type { publicOperatorAdmin } from "@kintone-storage/openapi-types";

export type AppBindingModeId =
  publicOperatorAdmin.components["schemas"]["ModeId"];
export type RecordFolderLabelFieldCandidate =
  publicOperatorAdmin.components["schemas"]["RecordFolderLabelFieldCandidate"];

export const SETUP_METHOD_DESCRIPTIONS = {
  easy: "kintone管理者アカウントを入力すると、プラグインのインストールやAPIトークンの発行、設定の反映を自動で行えます。",
  manual: "手順に沿って、プラグインのインストールやAPIトークンの発行、設定の反映を手動で行います。",
} as const;

export interface ParsedKintoneAppUrl {
  hostname: string;
  subdomain: string;
  secureAccess: boolean;
  appId: string;
  isGuestSpace: boolean;
  guestSpaceId: string | null;
}

export interface EditableFieldBinding {
  selected: boolean;
  fieldBindingKey: string;
  fieldCode: string;
  fieldLabel: string;
  targetScopeKind: "root_field" | "subtable_row";
  subtableFieldCode: string;
  tableLabel: string;
  customAttachmentEnabled: boolean;
  thumbnailEnabled: boolean;
  thumbnailSizePx: string;
  importedFromKintoneAt: string | null;
  importSourceState: "imported_default" | "user_overridden" | "reimported" | "source_unavailable";
  fieldSourceStatus: "available" | "deleted";
}

export const APP_BINDING_MODE_LABELS: Record<AppBindingModeId, string> = {
  attachment_compatible: "添付ファイル互換モード",
  shared_storage: "共有ストレージモード",
};

export interface AppBindingModeMeta {
  modeId: AppBindingModeId;
  label: string;
  description: string;
}

/**
 * 利用モード選択 UI で使うメタ情報（ラベル＋利用者視点の説明）。
 * 表示順は配列の順序に従う。
 */
export const APP_BINDING_MODE_META: AppBindingModeMeta[] = [
  {
    modeId: "shared_storage",
    label: APP_BINDING_MODE_LABELS.shared_storage,
    description: "フォルダ構成は自分たちで管理。共有しているファイルをkintoneでも利用できます。",
  },
  {
    modeId: "attachment_compatible",
    label: APP_BINDING_MODE_LABELS.attachment_compatible,
    description: "通常の添付ファイルと同じ閲覧権限。保存先のフォルダ構成を意識せずに利用できます。",
  },
];

/**
 * kintone API トークンに必要な権限（発行画面で利用者がチェックする項目）。
 */
export const KINTONE_API_TOKEN_REQUIRED_PERMISSIONS = [
  "アプリ管理",
  "レコード閲覧",
  "レコード編集",
] as const;

/**
 * kintone の API トークン発行 / 確認画面への直リンクを生成する。
 * ゲストスペースのアプリにも対応する。
 */
export function buildKintoneApiTokenAdminUrl(params: {
  subdomain: string;
  appId: string;
  guestSpaceId?: string | null;
  secureAccess?: boolean;
}): string | null {
  const subdomain = String(params.subdomain ?? "").trim();
  const appId = String(params.appId ?? "").trim();
  if (!subdomain || !appId) {
    return null;
  }
  const guestSegment = params.guestSpaceId
    ? `/guest/${encodeURIComponent(String(params.guestSpaceId))}`
    : "";
  const host = buildKintoneHost(subdomain, params.secureAccess === true);
  return host
    ? `https://${host}/k${guestSegment}/admin/app/apitoken?app=${appId}`
    : null;
}

export function buildKintoneHost(
  subdomain: string,
  secureAccess: boolean,
): string | null {
  const normalized = String(subdomain ?? "").trim().toLowerCase();
  if (
    normalized.length < 3 ||
    normalized.length > 63 ||
    !/^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])?$/.test(normalized)
  ) {
    return null;
  }
  return secureAccess
    ? `${normalized}.s.cybozu.com`
    : `${normalized}.cybozu.com`;
}

function normalizeHostname(hostname: string): string {
  return String(hostname ?? "").trim().toLowerCase();
}

function ensureUrl(input: string): URL {
  const raw = String(input ?? "").trim();
  if (!raw) {
    throw new Error("kintone URL を入力してください。");
  }
  const withScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(raw)
    ? raw
    : `https://${raw}`;
  return new URL(withScheme);
}

function deriveKintoneHost(hostname: string): {
  subdomain: string;
  secureAccess: boolean;
} | null {
  const normalized = normalizeHostname(hostname);
  const match = /^([a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])?)(\.s)?\.cybozu\.com$/.exec(
    normalized,
  );
  return match?.[1] && match[1].length >= 3
    ? { subdomain: match[1], secureAccess: match[2] === ".s" }
    : null;
}

function pickQueryAppId(url: URL): string {
  for (const key of ["app", "appId", "id"]) {
    const value = String(url.searchParams.get(key) ?? "").trim();
    if (/^[0-9]+$/.test(value)) {
      return value;
    }
  }
  return "";
}

export function parseKintoneAppUrl(input: string): ParsedKintoneAppUrl {
  const url = ensureUrl(input);
  const hostname = normalizeHostname(url.hostname);
  const host = deriveKintoneHost(hostname);

  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    url.port ||
    !hostname ||
    !host
  ) {
    throw new Error("cybozu.com の kintoneアプリ URL を入力してください。");
  }
  const { subdomain, secureAccess } = host;

  const segments = url.pathname.split("/").filter(Boolean);

  if (segments.length >= 4 && segments[0] === "k" && segments[1] === "guest") {
    const guestSpaceId = String(segments[2] ?? "").trim();
    const appId = String(segments[3] ?? "").trim();

    if (!/^[0-9]+$/.test(guestSpaceId) || !/^[0-9]+$/.test(appId)) {
      throw new Error("ゲストスペース URL からアプリ ID を抽出できません。");
    }

    return {
      hostname,
      subdomain,
      secureAccess,
      appId,
      isGuestSpace: true,
      guestSpaceId,
    };
  }

  if (segments.length >= 2 && segments[0] === "k") {
    const runtimeAppId = String(segments[1] ?? "").trim();
    if (/^[0-9]+$/.test(runtimeAppId)) {
      return {
        hostname,
        subdomain,
        secureAccess,
        appId: runtimeAppId,
        isGuestSpace: false,
        guestSpaceId: null,
      };
    }
  }

  if (segments.length >= 4 && segments[0] === "k" && segments[1] === "admin" && segments[2] === "app") {
    const pathAppId = String(segments[3] ?? "").trim();
    if (/^[0-9]+$/.test(pathAppId)) {
      return {
        hostname,
        subdomain,
        secureAccess,
        appId: pathAppId,
        isGuestSpace: false,
        guestSpaceId: null,
      };
    }
  }

  const queryAppId = pickQueryAppId(url);
  if (queryAppId) {
    return {
      hostname,
      subdomain,
      secureAccess,
      appId: queryAppId,
      isGuestSpace: false,
      guestSpaceId: null,
    };
  }

  throw new Error(
    "runtime URL または設定 URL からアプリ ID を抽出できません。URL を確認してください。",
  );
}

export function getAppBindingModeLabel(modeId: string | undefined): string {
  if (!modeId) {
    return "利用モード不明";
  }
  return APP_BINDING_MODE_LABELS[modeId as AppBindingModeId] ?? modeId;
}

export function normalizeAppBaseFolder(input: string, defaultValue = "/"): string {
  const trimmed = String(input ?? "").trim();
  if (!trimmed) {
    return defaultValue;
  }
  if (!trimmed.startsWith("/") || trimmed.includes("\\") || /\/{2,}/.test(trimmed)) {
    return "";
  }
  const normalized = trimmed.length > 1 ? trimmed.replace(/\/$/, "") : trimmed;
  const segments = normalized.split("/").filter(Boolean);
  if (segments.some((segment) => segment === "." || segment === "..")) {
    return "";
  }
  return normalized;
}

export function normalizeRequiredAppBaseFolder(input: string): string | null {
  const trimmed = String(input ?? "").trim();
  if (!trimmed) {
    return null;
  }
  return normalizeAppBaseFolder(trimmed, "") || null;
}

export function defaultAppBaseFolderForMode(input: {
  modeId: AppBindingModeId;
  kintoneAppId: string;
}): string {
  if (input.modeId === "shared_storage") {
    return "/";
  }
  const appIdSegment = input.kintoneAppId.trim();
  if (!appIdSegment) {
    return "";
  }
  return `/${appIdSegment}`;
}

export function buildFieldPlacementScopePath(
  appBaseFolder: string,
  modeId: AppBindingModeId,
  fieldBinding: Pick<
    EditableFieldBinding,
    "fieldCode" | "fieldLabel" | "targetScopeKind" | "subtableFieldCode" | "tableLabel"
  >,
) {
  const appBase = normalizeAppBaseFolder(appBaseFolder).replace(/^\/+|\/+$/g, "");
  const fieldSegment =
    fieldBinding.fieldLabel.trim() || fieldBinding.fieldCode.trim() || "field";
  const tableSegment =
    String(fieldBinding.tableLabel ?? "").trim() ||
    String(fieldBinding.subtableFieldCode ?? "").trim();
  const segments =
    modeId === "attachment_compatible"
      ? [
          appBase,
          "レコード",
          ...(fieldBinding.targetScopeKind === "subtable_row" && tableSegment
            ? ["テーブル", tableSegment, "行"]
            : []),
          "フィールド",
          fieldSegment,
        ]
      : [
          appBase,
          ...(fieldBinding.targetScopeKind === "subtable_row" && tableSegment
            ? ["テーブル", tableSegment]
            : ["フィールド"]),
          fieldSegment,
        ];
  return `/${segments.filter(Boolean).join("/")}`;
}

export function buildPlacementPreviewLabel(
  storageDisplayRoot: string | null | undefined,
  scopePath: string,
) {
  const root = String(storageDisplayRoot ?? "").trim();
  const normalizedScope = normalizeAppBaseFolder(scopePath).replace(/^\/+/, "");
  if (!root) {
    return `/${normalizedScope}`;
  }
  const cleanedRoot = root.replace(/[\\/]+$/g, "");
  const separator = cleanedRoot.includes("\\") ? "\\" : "/";
  return `${cleanedRoot}${separator}${normalizedScope.replace(/\//g, separator)}`;
}

export function getRecordFolderKindLabel(value: string) {
  switch (value) {
    case "record_id":
      return "レコードID";
    case "record_label_field":
      return "フィールド値ラベル";
    default:
      return "未設定";
  }
}

export type ProbeItem =
  publicOperatorAdmin.components["schemas"]["FieldSettingImportCandidate"];
